import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { RadioButton } from "primereact/radiobutton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import localeText from "../../../../../locale";
import { cloudinaryInstance } from "../../../../../utilities/Cloudinary/cloudinaryInstance";
import { channelService, channelUrl } from "../../../../Channel/channelConfig";
import { errorText, isOk, money, percentText } from "../../../../Channel/channelUtils";
import StoreSettingsFieldRow from "./StoreSettingsFieldRow";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "");

const ProductRow = ({ product, checked, disabled, onToggle }) => {
  const price = product.promotionalPriceUnique > 0 ? product.promotionalPriceUnique : product.priceUnique;
  const inputId = `channel-product-${product.id}`;

  return (
    <div
      className="flex align-items-center gap-3 py-3"
      style={{ borderBottom: "1px solid #E5E7EB", opacity: product.eligible ? 1 : 0.75 }}
    >
      <Checkbox inputId={inputId} checked={checked} disabled={disabled} onChange={() => onToggle(product.id)} />
      {product.imageid1 && (
        <AdvancedImage
          cldImg={cloudinaryInstance.image(product.imageid1).resize(fill().width(96).height(96)).format("webp")}
          style={{ width: 48, height: 48, borderRadius: 6, objectFit: "cover", flex: "0 0 48px" }}
          alt=""
        />
      )}
      <label htmlFor={inputId} className="flex-1" style={{ minWidth: 0, cursor: disabled ? "default" : "pointer" }}>
        <div className="font-medium" style={{ color: "#181D27" }}>
          {product.title}
        </div>
        <div className="text-sm" style={{ color: "#535862" }}>
          {money(price, product.currency)}
        </div>
        {!product.eligible && (
          <div className="text-sm" style={{ color: "#B42318" }}>
            {product.reasons.map((reason) => localeText(`channelReason${reason}`) || reason).join("; ")}
          </div>
        )}
      </label>
    </div>
  );
};

// Aba ScontiPoste nas configuracoes da loja: adesao do Manager ao canal e escolha dos produtos
const StoreSettingsChannelTab = () => {
  const { showErrorToast, showSuccessToast } = useToastContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [allProducts, setAllProducts] = useState(true);
  const [selected, setSelected] = useState([]);

  const apply = useCallback((result) => {
    setData(result);
    setAllProducts(result.joined ? result.allProducts : true);
    setSelected(result.products.filter((p) => p.selected && p.eligible).map((p) => p.id));
  }, []);

  const handle = useCallback(
    async (request, successMessage) => {
      setSaving(true);
      try {
        const res = await request();
        if (isOk(res)) {
          apply(res.result);
          if (successMessage) showSuccessToast({ detail: successMessage });
        } else {
          showErrorToast({ detail: errorText(res) });
        }
      } catch (e) {
        showErrorToast({ detail: errorText(e) });
      } finally {
        setSaving(false);
        setLoading(false);
      }
    },
    [apply, showErrorToast, showSuccessToast]
  );

  useEffect(() => {
    handle(() => channelService.sellerMe());
  }, [handle]);

  const eligibleProducts = useMemo(() => (data?.products || []).filter((p) => p.eligible), [data]);
  const otherProducts = useMemo(() => (data?.products || []).filter((p) => !p.eligible), [data]);

  if (loading) {
    return (
      <div className="flex justify-content-center p-5">
        <i className="pi pi-spin pi-spinner text-2xl" aria-label={localeText("channelLoading")} />
      </div>
    );
  }

  if (!data) return null;

  const percent = percentText(data.joined ? data.termsCashbackPercent : data.cashbackPercent);

  const toggle = (id) =>
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const join = () => {
    if (!acceptTerms) {
      showErrorToast({ detail: localeText("channelSellerAcceptRequired") });
      return;
    }
    handle(() => channelService.sellerJoin({ acceptTerms: true, allProducts }), "channelSellerJoinedToast");
  };

  const leave = () =>
    confirmDialog({
      message: localeText("channelSellerLeaveConfirm"),
      header: localeText("channelSellerLeave"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handle(() => channelService.sellerLeave(), "channelSellerLeft"),
    });

  const saveProducts = () =>
    handle(() => channelService.sellerProducts({ allProducts, productIds: allProducts ? [] : selected }), "channelSellerSaved");

  const modeSelector = (
    <div className="flex flex-column gap-3">
      <div className="flex align-items-center gap-2">
        <RadioButton inputId="channel-mode-all" checked={allProducts} onChange={() => setAllProducts(true)} />
        <label htmlFor="channel-mode-all">{localeText("channelSellerModeAll")}</label>
      </div>
      <div className="flex align-items-center gap-2">
        <RadioButton inputId="channel-mode-selected" checked={!allProducts} onChange={() => setAllProducts(false)} />
        <label htmlFor="channel-mode-selected">{localeText("channelSellerModeSelected")}</label>
      </div>
    </div>
  );

  const productList = (
    <div>
      {eligibleProducts.length === 0 && (
        <p className="text-sm" style={{ color: "#535862" }}>
          {localeText("channelSellerNoEligible")}
        </p>
      )}
      {eligibleProducts.map((product) => (
        <ProductRow
          key={product.id}
          product={product}
          checked={allProducts || selected.includes(product.id)}
          disabled={allProducts || !data.joined}
          onToggle={toggle}
        />
      ))}
      {otherProducts.map((product) => (
        <ProductRow key={product.id} product={product} checked={false} disabled onToggle={toggle} />
      ))}
    </div>
  );

  return (
    <div className="flex flex-column">
      <ConfirmDialog />

      <StoreSettingsFieldRow label={localeText("channelSellerTitle")} help={localeText("channelSellerIntro")}>
        {!data.sellerTypeAllowed ? (
          <p className="m-0">{localeText("channelSellerOnlyManagers")}</p>
        ) : data.joined ? (
          <div className="flex flex-column gap-3">
            <p className="m-0 font-medium">
              {localeText("channelSellerJoined", { date: formatDate(data.termsAcceptedDate), percent })}
            </p>
            <p className="m-0 text-sm" style={{ color: "#535862" }}>
              {localeText("channelSellerListedCount", { count: data.listedCount })}
            </p>
            {data.termsOutdated && (
              <div className="flex flex-column gap-2 p-3 border-round" style={{ background: "#FFF6E5" }} role="alert">
                <span>{localeText("channelSellerTermsOutdated", { percent: percentText(data.cashbackPercent) })}</span>
                <Button
                  label={localeText("channelSellerAcceptNew", { percent: percentText(data.cashbackPercent) })}
                  className="align-self-start"
                  loading={saving}
                  onClick={() =>
                    handle(
                      () => channelService.sellerJoin({ acceptTerms: true, allProducts: data.allProducts }),
                      "channelSellerJoinedToast",
                    )
                  }
                />
              </div>
            )}
            {!data.channelActive && (
              <p className="m-0 text-sm" style={{ color: "#535862" }}>
                {localeText("channelSellerChannelInactive")}
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              {data.channelActive && (
                <a href={channelUrl()} target="_blank" rel="noreferrer" className="p-button p-button-outlined no-underline">
                  {localeText("channelSellerOpenShowcase")}
                </a>
              )}
              <Button
                label={localeText("channelSellerLeave")}
                className="p-button-text p-button-danger"
                onClick={leave}
                disabled={saving}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-column gap-3">
            <p className="m-0 font-medium">{localeText("channelSellerEligibleCount", { count: data.eligibleCount })}</p>
            <div>
              <div className="font-semibold mb-2">{localeText("channelSellerTerms")}</div>
              <ul className="m-0 pl-4 flex flex-column gap-2" style={{ color: "#344054" }}>
                <li>{localeText("channelSellerTerm1", { percent })}</li>
                <li>{localeText("channelSellerTerm2")}</li>
                <li>{localeText("channelSellerTerm3")}</li>
                <li>{localeText("channelSellerTerm4")}</li>
              </ul>
            </div>
            <div className="flex align-items-center gap-2">
              <Checkbox inputId="channel-accept" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.checked)} />
              <label htmlFor="channel-accept">{localeText("channelSellerAccept")}</label>
            </div>
          </div>
        )}
      </StoreSettingsFieldRow>

      {data.sellerTypeAllowed && (
        <StoreSettingsFieldRow label={localeText("channelSellerMode")}>
          <div className="flex flex-column gap-3">
            {modeSelector}
            {data.joined ? (
              <Button
                label={localeText("channelSellerSave")}
                onClick={saveProducts}
                loading={saving}
                className="align-self-start"
              />
            ) : (
              <Button
                label={localeText("channelSellerJoin")}
                onClick={join}
                loading={saving}
                disabled={!acceptTerms}
                className="align-self-start"
              />
            )}
          </div>
        </StoreSettingsFieldRow>
      )}

      {data.sellerTypeAllowed && (
        <StoreSettingsFieldRow
          label={localeText("channelSellerProducts")}
          help={localeText("channelSellerEligibleCount", { count: data.eligibleCount })}
        >
          {productList}
        </StoreSettingsFieldRow>
      )}
    </div>
  );
};

export default StoreSettingsChannelTab;
